import { useEffect, useRef, useState } from 'react';
import { useSession } from '../lib/authClient';

const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: 'trainer' | 'client';
}

export function useAuth() {
  const { data: session, isPending, error } = useSession();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Używamy prymitywnego stringa jako zależności – referencja do sessionUser zmienia się
  // na każdym renderze, co powoduje nieskończoną pętlę efektów.
  const sessionEmail = session?.user?.email ?? null;
  const sessionUser = session?.user ?? null;

  // Ref blokuje wielokrotne fetche nawet jeśli efekt odpali się dwukrotnie
  const fetchedEmailRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    // Brak użytkownika – wyczyść profil
    if (!sessionEmail) {
      fetchedEmailRef.current = null;
      isFetchingRef.current = false;
      setProfile(null);
      return;
    }

    // Już pobraliśmy profil dla tego emaila – nie rób nic
    if (fetchedEmailRef.current === sessionEmail) return;

    // Już trwa fetch – nie startuj kolejnego
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    fetchedEmailRef.current = sessionEmail;
    setProfileLoading(true);

    fetch(`${API_BASE_URL}/users/me`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch profile');
        return res.json();
      })
      .then((data: UserProfile) => {
        setProfile(data);
      })
      .catch(() => {
        // Fallback – rola 'client' z danych sesji
        if (session?.user) {
          setProfile({
            id: session.user.id,
            email: session.user.email,
            name: session.user.name,
            role: 'client',
          });
        }
      })
      .finally(() => {
        isFetchingRef.current = false;
        setProfileLoading(false);
      });
  }, [sessionEmail]); // ← tylko prymityw, nie obiekt

  const user = sessionUser
    ? { ...sessionUser, role: profile?.role ?? ('client' as const) }
    : null;

  return {
    user,
    profile,
    session: session ?? null,
    isLoading: isPending || profileLoading,
    error: error?.message ?? null,
    isTrainer: profile?.role === 'trainer',
    refreshProfile: () => {
      // Reset – kolejny render wywoła ponowny fetch
      fetchedEmailRef.current = null;
    },
  };
}

// Legacy compatibility
export function useAuthStore(selector?: (state: any) => any) {
  const { user, session } = useAuth();

  const state = {
    currentProfileId: session?.user?.id ?? null,
    currentProfile: () =>
      user
        ? {
          id: user.id,
          name: user.name,
          role: (user as any).role ?? 'client',
          createdAt: (user as any).createdAt,
        }
        : null,
    logout: async () => {
      const { authClient } = await import('../lib/authClient');
      await authClient.signOut();
    },
  };

  if (selector) return selector(state);
  return state;
}

export { useSession } from '../lib/authClient';
